import { Link } from 'react-router-dom'

interface PortfolioSite {
  name: string
  domain: string
  url: string
  tagline: string
  description: string
  services: string[]
  gradientFrom: string
  gradientTo: string
  monogram: string
}

const PORTFOLIO: PortfolioSite[] = [
  {
    name: 'Lawdiem',
    domain: 'dev.lawdiem.com',
    url: 'https://dev.lawdiem.com',
    tagline: 'Software for the practice of law',
    description:
      'Ten services for litigators on one matter file — engagement and intake, the case file, billing and trust accounting, court-notice calendaring, discovery from pleadings through production, cite-checking, apposite-case search, and live hearing rebuttal.',
    services: ['Product design', 'Full-stack build', 'AWS hosting', 'AI features'],
    gradientFrom: '#1e3a5f',
    gradientTo: '#0ea5e9',
    monogram: 'L',
  },
  {
    name: 'Easy Tax Protest',
    domain: 'easytaxprotest.com',
    url: 'https://easytaxprotest.com',
    tagline: 'Texas property tax protest services',
    description:
      'Evidence-based property tax protests by registered consultants, with online signup in minutes and a no-savings-no-fee model — backed by a county appraisal-district data pipeline and a deployed property-facts API.',
    services: ['Client portal', 'CAD data pipeline', 'Property-facts API', 'AWS hosting'],
    gradientFrom: '#b45309',
    gradientTo: '#f59e0b',
    monogram: 'E',
  },
  {
    name: 'Ironrock Properties',
    domain: 'ironrockproperties.com',
    url: 'https://ironrockproperties.com',
    tagline: 'Property management & real estate in Central Texas',
    description:
      'Brokerage and property-management site for a firm with over 20 years of experience — services, listings, and owner resources, backed by internal CMA and valuation tooling.',
    services: ['Marketing site', 'CMA tooling', 'AWS hosting'],
    gradientFrom: '#374151',
    gradientTo: '#78716c',
    monogram: 'I',
  },
  {
    name: 'Hertabrand',
    domain: 'main.d2z4esb5ihh9rq.amplifyapp.com',
    url: 'https://main.d2z4esb5ihh9rq.amplifyapp.com',
    tagline: 'Master stylist, educator & The 6-Figure Masterclass',
    description:
      'Brand site and sales funnel for an Austin salon and education business — a full masterclass landing page (tiered pricing, curriculum, testimonials, FAQ) feeding Stripe enrollment, plus customizable services, transformation galleries, and booking calls-to-action styled to the brand’s own palette and artwork.',
    services: ['Brand site', 'Sales funnel', 'Next.js static build', 'Amplify hosting'],
    gradientFrom: '#ff4d94',
    gradientTo: '#00a35e',
    monogram: 'H',
  },
]

const OFFERINGS = [
  {
    icon: '🎨',
    title: 'Design & Build',
    body: 'Custom-designed sites and web apps — no templates. Modern stacks (Next.js, React, Tailwind) with fast static builds where they fit and full applications where they don’t.',
  },
  {
    icon: '☁️',
    title: 'AWS Hosting & Domains',
    body: 'Deployed on AWS Amplify with global CDN, SSL, and Route 53 DNS — the same infrastructure running every site below, managed end to end.',
  },
  {
    icon: '🔐',
    title: 'Portals & Integrations',
    body: 'Authentication, client portals, payments, booking, and data pipelines — Cognito, Lambda, DynamoDB, and third-party APIs wired into one coherent product.',
  },
  {
    icon: '🤖',
    title: 'Automation & AI',
    body: 'Email alerting, scheduled jobs, document pipelines, and Claude-powered features — the working systems behind Lawdiem and Easy Tax Protest.',
  },
]

const PROCESS = [
  { step: '01', title: 'Discover', body: 'Your business, your customers, and what the site has to do.' },
  { step: '02', title: 'Design', body: 'Brand-true layouts you approve before a line of code.' },
  { step: '03', title: 'Build', body: 'Fast, accessible, responsive — reviewed with you as it takes shape.' },
  { step: '04', title: 'Launch & Host', body: 'Deployed on AWS with SSL and DNS handled, then kept healthy.' },
]

export default function NVenturesPage() {
  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          to="/dashboard"
          className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <span className="text-3xl">🌐</span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">NVentures</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            Web design, development & hosting — custom sites and web apps, built and run on AWS
          </p>
        </div>
      </div>

      {/* Pitch banner */}
      <div
        className="rounded-xl p-6 sm:p-8 mb-8 text-white"
        style={{ background: 'linear-gradient(120deg, #0f172a 0%, #1e293b 55%, #134e4a 100%)' }}
      >
        <h2 className="text-xl sm:text-2xl font-bold">
          From first sketch to a live domain — one studio, end to end.
        </h2>
        <p className="mt-3 max-w-3xl text-slate-300 leading-relaxed text-sm sm:text-base">
          NVentures designs, builds, and hosts websites and web applications for small businesses and
          professional practices. Every project below is in production today: designed, developed,
          deployed, and operated on AWS — brand sites, client portals, data pipelines, and full
          practice-management software.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a
            href="mailto:nathanaelbrown@gmail.com?subject=NVentures%20project%20inquiry"
            className="inline-flex items-center gap-2 rounded-lg bg-white text-slate-900 px-5 py-2.5 text-sm font-semibold hover:bg-slate-200 transition-colors"
          >
            Start a project
          </a>
          <a
            href="#portfolio"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
          >
            See the work ↓
          </a>
        </div>
      </div>

      {/* Portfolio */}
      <h2 id="portfolio" className="text-lg font-semibold text-slate-900 dark:text-white mb-4 scroll-mt-24">
        Production work
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        {PORTFOLIO.map((site) => (
          <a
            key={site.domain}
            href={site.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden hover:border-slate-400 dark:hover:border-slate-500 transition-all hover:shadow-lg hover:shadow-slate-300/50 dark:hover:shadow-slate-900/50"
          >
            <div
              className="h-24 flex items-end justify-between px-5 pb-3"
              style={{ background: `linear-gradient(120deg, ${site.gradientFrom}, ${site.gradientTo})` }}
            >
              <span className="text-4xl font-black text-white/90 leading-none">{site.monogram}</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/25 px-2.5 py-1 text-xs font-medium text-white">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
                Live
              </span>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {site.name}
                  </h3>
                  <p className="text-xs font-mono text-slate-500 dark:text-slate-400">{site.domain}</p>
                </div>
                <svg
                  className="w-4 h-4 mt-1.5 flex-shrink-0 text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </div>
              <p className="mt-1 text-sm font-medium text-slate-600 dark:text-slate-300">{site.tagline}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-500 dark:text-slate-400">{site.description}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {site.services.map((s) => (
                  <span
                    key={s}
                    className="px-2 py-0.5 bg-slate-200/50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 text-xs rounded-md"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* Offerings */}
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">What NVentures does</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
        {OFFERINGS.map((o) => (
          <div
            key={o.title}
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{o.icon}</span>
              <h3 className="font-semibold text-slate-900 dark:text-white">{o.title}</h3>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-500 dark:text-slate-400">{o.body}</p>
          </div>
        ))}
      </div>

      {/* Process */}
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">How a project runs</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {PROCESS.map((p) => (
          <div
            key={p.step}
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5"
          >
            <span className="text-xs font-bold tracking-widest text-blue-600 dark:text-blue-400">{p.step}</span>
            <h3 className="mt-1 font-semibold text-slate-900 dark:text-white">{p.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-500 dark:text-slate-400">{p.body}</p>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 sm:p-8 text-center">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Have a project in mind?</h2>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
          A new site, a rebuild, a client portal, or hosting for something you already have — tell me
          what you&apos;re after and I&apos;ll come back with a plan and a quote.
        </p>
        <a
          href="mailto:nathanaelbrown@gmail.com?subject=NVentures%20project%20inquiry"
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 text-sm font-semibold transition-colors"
        >
          nathanaelbrown@gmail.com
        </a>
      </div>
    </div>
  )
}
